import { z } from 'zod';

/**
 * Erlaubte Dateitypen im Dokumentenmanagement (ADR 0021). Allowlist: nur gängige
 * Office-/PDF-/Bild-/Archiv-/CAD-Formate für QM, Verträge, Zeichnungen etc.
 */
export const MIME_ALLOWLIST = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'image/tiff',
  'application/zip',
  'application/x-zip-compressed',
  'application/acad', // DWG
  'image/vnd.dwg',
  'application/dxf',
  'application/octet-stream', // STEP/STL u. a. CAD-Exporte ohne spezifischen MIME
] as const;

/** Maximale Dateigröße (Default 100 MB); per Env `DMS_MAX_FILE_SIZE_BYTES` überschreibbar. */
export const MAX_FILE_SIZE_BYTES = 104_857_600;

/**
 * Sicherer Dateiname (Basename, kein Pfad): keine Pfadtrenner, kein `..`, keine
 * Steuerzeichen/NUL. Der Name landet im Speicherpfad (WebDAV) — ohne diese
 * Prüfung könnte `../../…` aus dem Dokumenten-Root ausbrechen (Path Traversal).
 */
export const safeFileNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(255)
  // eslint-disable-next-line no-control-regex
  .regex(/^[^/\\\x00-\x1f\x7f]+$/, 'Dateiname enthält unzulässige Zeichen (Pfadtrenner/Steuerzeichen).')
  .refine((name) => name !== '.' && name !== '..', 'Ungültiger Dateiname.');

export const uploadInitSchema = z.object({
  folderId: z.string().uuid(),
  name: safeFileNameSchema,
  mimeType: z.string().trim().min(1).max(255),
  size: z.number().int().min(0).max(MAX_FILE_SIZE_BYTES),
});
export type UploadInitInput = z.infer<typeof uploadInitSchema>;

export const uploadInitResponseSchema = z.object({
  documentId: z.string().uuid(),
  versionId: z.string().uuid(),
  uploadUrl: z.string().url(),
  objectKey: z.string(),
  expiresAt: z.string().datetime(),
});
export type UploadInitResponse = z.infer<typeof uploadInitResponseSchema>;

export const commitUploadSchema = z.object({
  versionId: z.string().uuid(),
  comment: z.string().trim().max(500).optional(),
});
export type CommitUploadInput = z.infer<typeof commitUploadSchema>;

export const versionResponseSchema = z.object({
  id: z.string().uuid(),
  version: z.number().int(),
  size: z.number().int(),
  mimeType: z.string(),
  checksum: z.string().nullable(),
  comment: z.string().nullable(),
  createdAt: z.string().datetime(),
  createdBy: z.string().uuid(),
});
export type VersionResponse = z.infer<typeof versionResponseSchema>;
